import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import {
  DARK_TAB_HIGHLIGHTS,
  highlightColor,
  TAB_HIGHLIGHTS
} from '@/navigation/components/tab/tabHighlights';
import { TAB_ICONS } from '@/navigation/components/tab/tabIcons';
import { TabRoute } from '@/navigation/types/navigation';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const ICON_SIZE = 18;
const INDICATOR_HEIGHT = 2;

const TAB_ORDER: TabRoute[] = [
  TabRoute.Start,
  TabRoute.Radio,
  TabRoute.Region,
  TabRoute.Profile
];

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    bar: {
      flexDirection: 'row',
      paddingHorizontal: 4,
      paddingTop: 4,
      paddingBottom: 6,
      gap: 2,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    tab: { flex: 1, alignItems: 'stretch' },
    pill: {
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      paddingVertical: 4,
      borderRadius: 8
    },
    label: { color: theme.colors.primary, fontSize: 8, lineHeight: 10 },
    indicator: {
      alignSelf: 'center',
      marginTop: 2,
      width: '50%',
      height: INDICATOR_HEIGHT,
      borderRadius: INDICATOR_HEIGHT,
      backgroundColor: theme.colors.primary
    },
    indicatorPlaceholder: { height: INDICATOR_HEIGHT, marginTop: 2 }
  });

type Props = {
  active?: TabRoute;
};

/**
 * Miniature of the real bottom navigation. Icons come from the shared
 * TAB_ICONS map used by BottomTabbar, so the tour never drifts from the app.
 */
export const TourTabBar: FC<Props> = ({ active }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.NAVIGATION, {
    keyPrefix: 'tabs'
  });
  const highlights = theme.dark ? DARK_TAB_HIGHLIGHTS : TAB_HIGHLIGHTS;

  return (
    <View style={styles.bar} testID="tour-tab-bar">
      {TAB_ORDER.map((key) => {
        const { icon: Icon, colorProp } = TAB_ICONS[key];
        const highlight = highlights[key];
        const isActive = key === active;

        return (
          <View key={key} style={styles.tab}>
            <View
              style={[
                styles.pill,
                highlight
                  ? { backgroundColor: highlightColor(theme, highlight) }
                  : undefined
              ]}
            >
              <Icon
                {...{ [colorProp]: theme.colors.primary }}
                width={ICON_SIZE}
                height={ICON_SIZE}
              />
              <Text
                style={styles.label}
                numberOfLines={1}
                maxFontSizeMultiplier={1}
              >
                {t(key)}
              </Text>
            </View>
            {isActive ? (
              <View
                style={styles.indicator}
                testID={`tour-tab-${key}-indicator`}
              />
            ) : (
              <View style={styles.indicatorPlaceholder} />
            )}
          </View>
        );
      })}
    </View>
  );
};
